'use client'

import { FiLogOut } from 'react-icons/fi'
import { FaCrown } from 'react-icons/fa6'
import { useNavigate } from 'react-router-dom'
import { getBrandLogoPath, getBrandProductName, isKlakklikkluk } from '../utils/brandUtils'
import { useAuthStore } from '../stores/authStore'
import { useState, useEffect } from 'react'
import { MdSpaceDashboard } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { LanguageSelector } from '@renderer/components'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import { useSettingsStore } from '../stores/settingsStore'
import { UpgradeModal } from './UpgradeModal'

type Props = {
  username: string
  credits: string | number
}

const ProjectNavbar = ({ username }: Props): React.JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const { activationCode } = useSettingsStore()
  const { type: packageType, isValid, proTrialExpiresAt } = useSubscriptionStore()
  const hasDeviceSubscription = Boolean(activationCode && isValid)
  const displayType = hasDeviceSubscription ? packageType : 'free'
  const [remainingTime, setRemainingTime] = useState<string | null>(null)

  const handleLogout = (): void => {
    logout()
    navigate('/login')
  }

  // ProTrial countdown logic (only when device subscription is active)
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    if (hasDeviceSubscription && displayType?.toLowerCase() === 'protrial' && proTrialExpiresAt) {
      const expiryDate = new Date(proTrialExpiresAt)

      const updateCountdown = (): void => {
        const now = new Date()
        const diff = expiryDate.getTime() - now.getTime()

        if (diff <= 0) {
          setRemainingTime(t('proTrial.expired'))
          if (timer) clearInterval(timer)
          return
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        if (days === 0) {
          setRemainingTime(t('proTrial.timeRemainingRealtime', { hours, minutes, seconds }))
        } else {
          setRemainingTime(t('proTrial.timeRemainingDays', { days }))
        }
      }

      updateCountdown()
      timer = setInterval(updateCountdown, 1000)
    } else {
      setRemainingTime(null)
    }

    return (): void => {
      if (timer) clearInterval(timer)
    }
  }, [hasDeviceSubscription, displayType, proTrialExpiresAt, t])

  return (
    <nav className="bg-whiteout bg-opacity-90 z-50">
      <div className="mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <a
              href={`${process.env.VITE_API_URL}`}
              className="text-xl font-medium text-black flex gap-2 items-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={getBrandLogoPath()}
                alt={t('projectNav.logoAlt', `${getBrandProductName()} Logo`)}
                height={40}
                width={150}
                className="object-contain"
              />
            </a>
            <LanguageSelector />
          </div>
          <div className="hidden md:flex space-x-4 items-center">
            <span className="text-black font-medium">
              {t('projectNav.greeting', 'Hello, {{username}}!', { username })}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleLogout}
              className="flex items-center text-carbon gap-2 hover:text-red-600 transition duration-300"
              title={t('common.logout', 'Logout')}
            >
              <FiLogOut className="w-5 h-5" />
            </button>
            <a
              href={`${
                isKlakklikkluk()
                  ? `https://admin.klakklikkluk.com/dashboard/projects`
                  : `${process.env.VITE_API_URL}/dashboard/projects`
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-carbon py-3 px-6 rounded-3xl hover:text-blueSparkle transition duration-300"
              title={t('nav.dashboard', 'Dashboard')}
            >
              <button title={t('nav.dashboard', 'Dashboard')}>
                <MdSpaceDashboard className="w-5 h-5" />
              </button>
            </a>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white transition-colors
              ${
                displayType.toLowerCase() === 'enterprise'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-indigo-600 hover:to-purple-700'
                  : displayType.toLowerCase() === 'professional'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                    : displayType.toLowerCase() === 'protrial'
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600'
                      : displayType.toLowerCase() === 'starter'
                        ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600'
                        : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
              }`}
              title={`${displayType && displayType.toLowerCase() !== 'free' ? displayType : t('common.upgrade', 'Upgrade')} ${t('projectNav.subscription', 'subscription')}`}
            >
              <FaCrown className="text-sm h-5 w-5" />
              <div className="flex flex-col text-left">
                <span>
                  {displayType &&
                  (displayType.toLowerCase() === 'protrial' || displayType.toLowerCase() !== 'free')
                    ? displayType.charAt(0).toUpperCase() + displayType.slice(1)
                    : t('common.upgrade', 'Upgrade')}
                </span>
                {displayType?.toLowerCase() === 'protrial' && remainingTime && (
                  <span className="text-xs -mt-1">({remainingTime})</span>
                )}
              </div>
            </button>
          </div>
        </div>
      </div>
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
    </nav>
  )
}

export default ProjectNavbar
